import videojs from 'video.js';
import { sanitizeHtmlString } from '../../util/SFMP.MiniUtility.js';
import { parseMessage, buildMessage, getApiState, mapOldLayoutOptions, BROKER_ACTIVATION_DATA, PlayState, PlayerState, ErrorCode } from './iframe/';
import { applyPlayerOptions } from '../bundled/loader/';
import { createOriginMatcherFromList } from '../../util/api-origin.js';
import { PresentationStatus } from '../../api/presentation';

const _allowedOrigins = new WeakMap();

const _universalOrigin = '*';
const _nonStandardOrigin = 'null'; // If the origin in question is not a scheme/host/port tuple, then return the literal string "null" and abort these steps.  http://www.w3.org/TR/html5/origin-0.html#unicode-serialization-of-an-origin

/**
 * The IFrame module.<br><br>
 * Note: this module is optional and might not be available on every MediasitePlayer instance.
 * @member {IFramePlugin|undefined} iframe
 * @memberof MediasitePlayer#
 */

/**
 * Relays player events to a parent window, and listens for messages from that parent.
 * @hideconstructor
 * @extends {external:"videojs.Plugin"}
 **/
class IFramePlugin extends videojs.getPlugin('plugin') {
    /**
     * 
     * @param {videojs.Player} player
     * @param {Object} [options]
     */
    constructor(player, options) {
        super(player, options);
        this.log('Loaded iframe plugin.'); 
    }

    /**
     * Initializes the IFrame plugin.
     * @param {Object} options
     * @param {Array<string>} options.allowedPlayerIFrameAPISites
     */
    init(options) {
        const allowedOrigins = Array.isArray(options.allowedPlayerIFrameAPISites) && options.allowedPlayerIFrameAPISites.length && options.allowedPlayerIFrameAPISites.slice() || _universalOrigin;
        _allowedOrigins.set(this, allowedOrigins);

        this.#registerPlayerListeners();

        this.handleMessageEvent = this.#handleMessageEvent.bind(this);
        window.addEventListener('message', this.handleMessageEvent);
    }

    /**
     * Returns true if communication is allowed with a parent window of the given origin; otherwise, returns false.
     * @param {string} origin
     * @param {string} [establishedBrokerOrigin]
     * @returns {boolean}
     */
    isAllowedBrokerOrigin(origin, establishedBrokerOrigin) {
        const allowedBrokerSites = _allowedOrigins.get(this);

        var matchedOrigin;

        if (establishedBrokerOrigin) {
            if (establishedBrokerOrigin == _universalOrigin) {
                matchedOrigin = establishedBrokerOrigin;
            } else {
                matchedOrigin = (establishedBrokerOrigin == origin) && origin;
            }
        } else {
            matchedOrigin = this.findMatchingOrigin(origin, allowedBrokerSites);
        }

        return !(!matchedOrigin);
    }

    /**
     * Checks for a matching origin.
     * @param {string} needleOrigin
     * @param {Array<string>} haystackSites
     * @returns {string}
     */
    findMatchingOrigin(needleOrigin, haystackSites) {
        var allowedOriginMatcher = createOriginMatcherFromList(haystackSites);
        var isAllowed = allowedOriginMatcher.match(needleOrigin);

        if (allowedOriginMatcher.match(needleOrigin)) {
            if (needleOrigin === _nonStandardOrigin || needleOrigin.indexOf('file:') == 0) { // for sending messages when window.document.location.protocol = file:
                return _universalOrigin;
            }

            return needleOrigin;
        }
    }

    #registerPlayerListeners() {
        //playcoverready
        if (this.player.loader().playCoverLoaded) {
            this.#handlePlayCoverLoaded();
        }
        else {
            this.one(this.player, 'playcoverloaded', this.#handlePlayCoverLoaded);
        }

        this.one(this.player, 'playcoverclick', () => this.#sendMessage('playcoverclick'));

        this.one(this.player, 'loadstart', this.#handleLoadStart);
        this.one(this.player, 'presentationloaded', this.#handlePresentationLoaded);

        //playstatechanged, playerstatechanged
        this.on(this.player, 'pause', this.#handlePause);
        this.on(this.player, 'playing', this.#handlePlaying);
        this.on(this.player, 'ended', this.#handlePresentationEnded);

        //livestatuschanged
        this.on(this.player, 'livestatuschanged', this.#handleLiveStatusChanged);

        //volumechanged
        this.on(this.player, 'volumechange', this.#handleVolumeChange);

        //currenttimechanged
        this.on(this.player, 'timeupdate', this.#handleTimeUpdate);

        //durationchanged
        this.on(this.player, 'durationchange', this.#handleDurationChange);

        //playbackratechanged
        this.on(this.player, 'ratechange', this.#handleRateChange);

        //captionchanged
        this.on(this.player, 'captionchanged', this.#handleCaptionChange);

        //chapterchanged
        this.on(this.player, 'chapterchanged', this.#handleChapterChange);

        //timedeventlistchanged
        //timedeventreached
        this.on(this.player, 'timedeventreached', this.#handleTimeEventReached);

        this.on(this.player, 'error', this.#handleError);

        //slidechanged
        //slideadded
        //visiblestreamschanged
        //fullscreeninvocationtypechanged
        //externalisfullscreenchanged
    }

    #handlePlayCoverLoaded() {
        this.#sendMessage('playcoverready')
    }

    #handleLoadStart() {
        this.#sendMessage('_broker_activated', BROKER_ACTIVATION_DATA);
        this.#sendMessage('_api_state', getApiState(this.player));

        this.#handleVolumeChange();
        //this.#handleRateChange();
        // Todo: visiblestreamschanged
        // Todo: slidechanged
        
    }

    #handlePresentationLoaded() {
        this.#sendMessage('_api_state', getApiState(this.player));
        this.#sendMessage('ready');
        this.#handleLiveStatusChanged(); // Legacy player sent livestatuschanged event after ready on live presentations
    }

    #handlePause() {
        this.#sendMessage('playstatechanged', { playState: PlayState.Paused });
        this.#sendMessage('playerstatechanged', { state: PlayerState.Paused, isLive: this.player.usingPlugin('live') });
    }

    #handlePlaying() {
        this.#sendMessage('playstatechanged', { playState: PlayState.Playing });
        this.#sendMessage('playerstatechanged', { state: PlayerState.Playing, isLive: this.player.usingPlugin('live') });
    }

    #handlePresentationEnded() {
        this.#sendMessage('playstatechanged', { playState: PlayState.MediaEnded });
        this.#sendMessage('playerstatechanged', { state: PlayerState.Ended, isLive: this.player.usingPlugin('live') });
    }

    #handleLiveStatusChanged() {
        this.player.ready(() => {
            let liveStatus;

            //if we're not live, return the 'On Demand' state for the live status
            if (this.player.live == null) {
                liveStatus = PresentationStatus.OnDemand;
            } else {
                liveStatus = this.player.live?.().status;
            }

            if (liveStatus) {
                this.#sendMessage('livestatuschanged', { liveStatus });
            }
        });
    }

    #handleVolumeChange() {
        this.#sendMessage('volumechanged', { volume: this.player.volume() * 100, isMuted: this.player.muted() }); // API expects a percent from 1-100%
    }

    #handleTimeUpdate() {
        this.#sendMessage('currenttimechanged', { currentTime: this.player.currentTime() })
    }

    #handleDurationChange() {
        this.#sendMessage('durationchanged', { duration: this.player.duration() });
    }

    #handleRateChange() {
        this.#sendMessage('playbackratechanged', { currentRate: this.player.playbackRate() });
    }

    /**
     * 
     * @param {any} event
     * @param {VTTCue[]} currentCaptions
     */
    #handleCaptionChange(event, currentCaptions) {
        const mediasiteCaptions = currentCaptions.map(cue => ({
            time: cue.startTime,
            endTime: cue.endTime,
            text: cue.text
        }))
        this.#sendMessage('captionchanged', { currentCaptions: mediasiteCaptions });
    }

    #handleChapterChange(event, currentChapter) {
        this.#sendMessage('chapterchanged', {
            chapterTime: currentChapter?.startTime ?? null,
            chapterTitle: currentChapter?.text ?? null
        });
    }

    #handleTimeEventReached(event, timedEvent) {
        const { id, time, type, payload } = timedEvent;
        this.#sendMessage('timedeventreached', {
            timedEventId: id,
            timedEventTime: time,
            timedEventType: type,
            timedEventPayload: payload
        });
    }

    #handleError(event) {
        const error = this.player.error();
        let errorCode;

        switch (error?.code) {
            case MediaError.MEDIA_ERR_ABORTED:
            case MediaError.MEDIA_ERR_NETWORK:
                break;
            case MediaError.MEDIA_ERR_DECODE:
                errorCode = ErrorCode.MediaPlayFailed;
                break;
            case MediaError.MEDIA_ERR_SRC_NOT_SUPPORTED:
                errorCode = ErrorCode.MediaNotSupported;
                break;
            default:
                errorCode = ErrorCode.MediaError;
                break;
        }

        if (errorCode) {
            this.#sendError(errorCode, {
                details: error?.message
            });
        }
        
    }

    #handleMessageEvent(event) {
        const { data, source: sourceWindow, origin: sourceOrigin } = event;
        const isAllowedOrigin = this.isAllowedBrokerOrigin(sourceOrigin);

        if (isAllowedOrigin) {
            //First, see if the message is even for the iframe plugin
            let parsedData;
            try {
                parsedData = parseMessage(data);
            }
            catch (err) {
                this.log(err.message);
                return;
            }
            //then, if it is, figure out what command to run
            const { command, params } = parsedData;
            try {                
                this.#handleCommand(command, params);
            }
            catch (err) {
                this.#sendError(ErrorCode.MethodError, {
                    details: err.message || err.description || err,
                    command,
                    params
                });
            }
        }
    }

    #sendMessage(message, data) {
        if (window.parent && window.parent.postMessage) {
            try {
                window.parent.postMessage(buildMessage(message, data), '*');
            }
            catch (e) {
                this.log('Unable to communicate with the Mediasite IFrame API: ');
                this.log.error(e);
            }
        }
    }

    #sendError(errorCode, data) {
        this.#sendMessage('error', { errorCode, ...data });
    }

    #handleCommand(command, params) {
        this.log(`IFrame API received command "${command}" with params: ${JSON.stringify(params)}`);
        const presentationLoaded = this.player.loader().presentationLoaded;
        const waiting = this.player.hasClass('vjs-waiting');
        if (waiting && !presentationLoaded) {
            if (command == 'setLayoutOptions') {
                const { options } = params[0];
                this.player.loader().loadPresentation().then((presentationData) => {
                    let quizzesEnabled
                    if (this.player.usingPlugin('quizzes')) {
                        quizzesEnabled = this.player.quizzes().enableSeeking;
                    }
                    else {
                        quizzesEnabled = null;
                    }
                    const mappedOptions = mapOldLayoutOptions(options, presentationData, quizzesEnabled);
                    applyPlayerOptions(this.player, mappedOptions);
                });
            }
            else {
                this.#sendError(ErrorCode.PlayerNotReady);
            }
        }
        else {
            switch (command) {
                case 'play': {
                    if (presentationLoaded) {
                        this.player.play();
                    }
                    else {
                        this.player.loader().loadPresentation();
                        this.player.play();
                    }
                    break;
                }
                case 'pause': {
                    if (presentationLoaded) {
                        this.player.pause();
                    }
                    break;
                }
                case 'stop': {
                    if (presentationLoaded) {
                        this.player.pause();
                    }
                    break;
                }
                case 'seekTo': {
                    if (presentationLoaded) {
                        const time = parseFloat(params[0]?.seconds);
                        this.player.currentTime(time);
                    }
                    break;
                }
                case 'setVolume': {
                    if (presentationLoaded) {
                        const { volume } = params[0];
                        this.player.volume(volume / 100); // API sends values from 0-100%, but the player needs 0.0-1.0
                    }
                    break;
                }
                case 'setPlaybackRate': {
                    if (presentationLoaded) {
                        const { rate } = params[0];
                        this.player.playbackRate(rate);
                    }
                    break;
                }
                case 'mute': {
                    if (presentationLoaded) {
                        this.player.muted(true);
                    }
                    break;
                }
                case 'unMute': {
                    if (presentationLoaded) {
                        this.player.muted(false);
                    }
                    break;
                }
                case 'unsafeAddTimedEvent': {
                    if (presentationLoaded) {
                        const { time, type, payload, id } = params[0];
                        this.player.timedEvents().add({
                            id,
                            time,
                            type,
                            payload
                        });
                    }
                    break;
                }
                case 'removeTimedEvent': {
                    if (presentationLoaded) {
                        const { id } = params[0];
                        this.player.timedEvents().remove(id);
                    }
                    break;
                }
                case 'setTimedEventMarkerOptions': {
                    if (presentationLoaded) {
                        const [id, { color, text }] = params;
                        const existingOptions = this.player.timelineMarkers().get(id);
                        const existingTimedEvent = this.player.timedEvents().get(id);
                        if (typeof existingOptions === 'object') {
                            this.player.timelineMarkers().update(id, { color: color, label: text });
                        } else if (typeof existingTimedEvent === 'object') {
                            this.player.timelineMarkers().add({ id: id, time: existingTimedEvent.time, label: text, color: color });
                        }
                    }
                    break;
                }
                case 'clearTimedEventMarkerOptions': {
                    if (presentationLoaded) {
                        const [id] = params;
                        const existingOptions = this.player.timelineMarkers().get(id);
                        this.player.timelineMarkers().remove(id);
                    }
                    break;
                }
                case 'setPlayCoverCustomHtml': {
                    if (!(presentationLoaded || waiting)) {
                        const { htmlString } = params[0];

                        const Component = videojs.getComponent('Component');
                        const playCoverCustomComponent = new Component(this.player);
                        playCoverCustomComponent.contentEl().innerHTML = sanitizeHtmlString(htmlString);

                        this.player.playcover().setPlaycoverCustomContent(playCoverCustomComponent, 0);
                        this.player.playcover().dimmed = true;
                    }
                    break;
                }
                case 'setLayoutOptions': {
                    const { options } = params[0];
                    this.player.loader().loadPresentation().then((presentationData) => {
                        let quizzesEnabled
                        if (this.player.usingPlugin('quizzes')) {
                            quizzesEnabled = this.player.quizzes().enableSeeking;
                        }
                        else {
                            quizzesEnabled = null;
                        }
                        const mappedOptions = mapOldLayoutOptions(options, presentationData, quizzesEnabled);
                        applyPlayerOptions(this.player, mappedOptions);
                    });
                    break;
                }
                default: {
                    this.#sendError(ErrorCode.MethodNotFound, {
                        command,
                        params
                    });
                }
            }
        }
    }

    dispose() {
        window.removeEventListener('message', this.handleMessageEvent);
    }
}

export { IFramePlugin };
